/**
 * Нативная (в валюте обязательства) сторона обычного роялти иностранной строки на экране
 * выставления. ЕДИНСТВЕННАЯ реализация формулы: колонка «Расчёт», «Итог», окно
 * подтверждения и снимок сегмента (`buildSegmentSnapshots`) читают её результат из строки
 * (`base_royalty_native`, `min_royalty_topup_native`, `estimated_royalty_native`), а не делят
 * рубли на курс сами.
 *
 * Правило — зеркало движка `sync_royalty_charge_from_accrual` (§1.8):
 *  - процентная часть = выручка в валюте обязательства × процент. Выручка собрана
 *    посуточно (каждый день по кросс-курсу своего дня), поэтому у неё нет «курса месяца»,
 *    которым можно было бы делить рубли;
 *  - минималка канонична в своей валюте: 1 000 AED из договора остаются 1 000 AED, курса
 *    в валюте обязательства ей не нужно вовсе;
 *  - рублёвые величины (корректировки, месячный top-up последнего сегмента) переводятся
 *    ЭФФЕКТИВНЫМ курсом сегмента = рубли выручки ÷ выручка в валюте обязательства — тем
 *    же, что движок кладёт в `royalty_charges.fx_rate`.
 * Курс конца месяца остаётся якорем FX_MISSING и фолбэком, когда выручки нет (нечем
 * взвешивать) — ровно как в движке.
 *
 * Почему это важно (замер PROD 05.09.2026): деление рублёвой базы на курс конца месяца
 * давало 3 392,17 AED при выручке 33 297,41 AED × 10,5 % = 3 496,23 AED, то есть занижало
 * долг на 3 %, и «Расчёт» переставал быть процентом от выручки в той же строке.
 */

export function roundNative(value: number): number {
  return Math.round(value * 100) / 100
}

/**
 * Эффективный курс обязательство→RUB сегмента, как `v_royalty_fx_rate` движка: рубли
 * выручки ÷ выручка в валюте обязательства, когда обе ненулевые (отрицательная выручка —
 * возвраты — тоже даёт положительный курс); без выручки — month-end; RUB → 1.
 */
export function resolveObligationEffectiveRate(input: {
  chargeCurrency: string
  revenueRub: number
  revenueObl: number
  monthEndRate: number | null
}): number | null {
  if (input.chargeCurrency === 'RUB') return 1
  if (input.revenueRub !== 0 && input.revenueObl !== 0) {
    const ratio = input.revenueRub / input.revenueObl
    if (Number.isFinite(ratio) && ratio > 0) return ratio
  }
  return input.monthEndRate != null && input.monthEndRate > 0 ? input.monthEndRate : null
}

/** Корректировки хранятся в рублях; в валюту обязательства — эффективным курсом, с клампом в 0 как у рублей. */
export function applyAdjustmentsNative(baseNative: number, adjustmentsRub: number, effectiveRate: number): number {
  if (adjustmentsRub === 0) return roundNative(baseNative)
  return Math.max(roundNative(baseNative + adjustmentsRub / effectiveRate), 0)
}

export interface ForeignRegularRoyaltyNativeInput {
  /** Выручка сегмента в валюте обязательства (посуточная сумма или сохранённый снимок). */
  revenueObl: number
  royaltyPercent: number
  /** Минимум договора, уже приведённый к валюте обязательства (0 — минимума нет). */
  minRoyaltyNative: number
  /** Single-segment месяц: `max(процент, минимум)`. Multi-segment: минималка — месячным top-up позже. */
  applyMinimum: boolean
  /** Сумма адресных корректировок ROYALTY в рублях (знаковая). */
  adjustmentsRub: number
  effectiveRate: number
  /** Сохранённый снимок (`royalty_amount_native`) перекрывает расчёт базы — «зафиксированное держится». */
  savedBaseNative?: number | null
}

export interface ForeignRegularRoyaltyNative {
  percentNative: number
  /** «Расчёт»: процент + доплата до минимума, без корректировок. */
  baseNative: number
  topupNative: number
  /** «Итог» до выставления: база с корректировками, не ниже нуля. */
  estimatedNative: number
}

export function computeForeignRegularRoyaltyNative(input: ForeignRegularRoyaltyNativeInput): ForeignRegularRoyaltyNative {
  const percentNative = roundNative((input.revenueObl * input.royaltyPercent) / 100)
  const topupNative = input.applyMinimum && input.minRoyaltyNative > 0
    ? Math.max(roundNative(input.minRoyaltyNative - percentNative), 0)
    : 0
  const computedBase = roundNative(percentNative + topupNative)
  const baseNative = input.savedBaseNative != null ? roundNative(input.savedBaseNative) : computedBase
  return {
    percentNative,
    baseNative,
    topupNative,
    estimatedNative: applyAdjustmentsNative(baseNative, input.adjustmentsRub, input.effectiveRate),
  }
}
